/**
 * Upload command with rich terminal UI
 */

import {
  CLIOptions,
  Command,
  ExitCode,
  NetworkError,
  AuthenticationError,
  UsageLimitError,
  Config,
  ProgressTracker,
} from '@/types';
import { configLoader, EnvironmentDetector } from '@/config';
import { DiscoveryResult, ReportDiscoveryService } from '@/core/discovery';
import { UploadService } from '@/services/upload';
import type { ReportUploadResponse } from '@/services/api';
import { createProgressTracker } from '@/utils/progress';
import { EnvironmentType, EnvironmentUtils } from '@/utils/env';
import { VERSION } from '@/version';
import {
  printHeader,
  printSuccess,
  printError,
  colors,
  formatKeyValue,
} from '@/utils/ui';
import { setVerbose, logger } from '@/utils/logger';

/**
 * Main upload command that orchestrates the entire upload process
 */
export class UploadCommand implements Command {
  public readonly name = 'upload';
  public readonly description = 'Upload Playwright test reports to TestDino';

  async execute(options: CLIOptions): Promise<void> {
    const tracker = createProgressTracker();

    try {
      // Print header
      printHeader(VERSION);

      // Validate configuration
      tracker.start('Validating configuration...');
      const config = configLoader.createConfig(options);
      setVerbose(config.verbose);
      tracker.succeed('Configuration validated');

      // Show configuration summary
      if (config.verbose) {
        this.printConfigSummary(config, options);
      }

      // Discover report files
      tracker.start('Discovering report files...');
      const discoveryService = new ReportDiscoveryService(
        options.reportDirectory
      );
      const discoveryResult = await discoveryService.discover(options);

      const reportCount = this.countReports(discoveryResult);
      tracker.succeed(
        `Found ${reportCount} ${reportCount === 1 ? 'report type' : 'report types'}`
      );

      // Show discovered files in verbose mode
      if (config.verbose) {
        this.printDiscoveredFiles(discoveryResult);
      }

      // Perform upload
      const uploadService = new UploadService(config);
      const uploadResponse = await uploadService.uploadWithFallback(
        discoveryResult.jsonReport,
        discoveryResult.htmlReport,
        discoveryResult.traceDir
      );

      // Build result URL
      const testdinoUrl = this.buildResultUrl(uploadResponse);

      // Show warnings if any
      if (uploadResponse.warnings?.length) {
        logger.blank();
        for (const warning of uploadResponse.warnings) {
          logger.warn(warning);
        }
      }

      // Success!
      printSuccess('Upload completed successfully!', testdinoUrl);
    } catch (error) {
      this.handleError(error, tracker);
    }
  }

  private printConfigSummary(config: Config, options: CLIOptions): void {
    const envInfo = EnvironmentDetector.getEnvironmentInfo();

    console.log();
    console.log(colors.bold('Configuration'));
    console.log(formatKeyValue('Environment', String(envInfo.type)));
    console.log(
      formatKeyValue(
        'CI/CD',
        envInfo.isCI ? `Yes (${envInfo.ciProvider})` : 'No'
      )
    );
    console.log(formatKeyValue('Report', options.reportDirectory));

    const uploadFlags: string[] = [];
    if (config.uploadFullJson) uploadFlags.push('full-json');
    if (config.uploadImages) uploadFlags.push('images');
    if (config.uploadVideos) uploadFlags.push('videos');
    if (config.uploadHtml) uploadFlags.push('html');
    if (config.uploadTraces) uploadFlags.push('traces');
    if (config.uploadFiles) uploadFlags.push('files');

    console.log(
      formatKeyValue(
        'Upload',
        uploadFlags.length ? uploadFlags.join(', ') : 'report only'
      )
    );
    console.log();
  }

  private printDiscoveredFiles(discoveryResult: DiscoveryResult): void {
    console.log();
    console.log(colors.bold('Discovered Files'));
    console.log(formatKeyValue('JSON', discoveryResult.jsonReport));
    if (discoveryResult.htmlReport) {
      console.log(formatKeyValue('HTML', discoveryResult.htmlReport));
    }
    if (discoveryResult.traceDir) {
      console.log(formatKeyValue('Traces', discoveryResult.traceDir));
    }
    console.log();
  }

  private countReports(discoveryResult: DiscoveryResult): number {
    let count = 1; // JSON is always present
    if (discoveryResult.htmlReport) count++;
    if (discoveryResult.traceDir) count++;
    return count;
  }

  private buildResultUrl(
    uploadResponse: ReportUploadResponse
  ): string | undefined {
    // Use viewUrl if provided by the API
    if (uploadResponse.viewUrl) {
      return uploadResponse.viewUrl;
    }

    // Construct URL from components if available
    if (
      uploadResponse.organizationId &&
      uploadResponse.projectId &&
      uploadResponse.testRunId
    ) {
      const envType = EnvironmentUtils.detectEnvironmentType();
      const baseUrl =
        envType === EnvironmentType.PRODUCTION
          ? 'https://app.testdino.com'
          : envType === EnvironmentType.STAGING
            ? 'https://staging.testdino.com'
            : 'http://localhost:3001';

      return `${baseUrl}/${uploadResponse.organizationId}/projects/${uploadResponse.projectId}/test-runs/${uploadResponse.testRunId}`;
    }

    // No URL available
    return undefined;
  }

  private handleError(error: unknown, tracker: ProgressTracker): never {
    tracker.fail('Upload failed');

    if (error instanceof AuthenticationError) {
      printError('Authentication Failed', error.message, [
        'Verify your API token is correct',
        'Check that your token has upload permissions',
        "Ensure you're using the right environment token",
      ]);
      process.exit(ExitCode.AUTHENTICATION_ERROR);
    }

    if (error instanceof NetworkError) {
      printError('Network Error', error.message, [
        'Check your internet connection',
        'Verify the API endpoint is accessible',
        'Try again in a few moments',
      ]);
      process.exit(ExitCode.NETWORK_ERROR);
    }

    if (error instanceof UsageLimitError) {
      printError('Usage Limit Exceeded', error.message, [
        'Enable auto-borrow in your organization settings',
        'Request more allocation from your organization admin',
        'Upgrade your plan for higher limits',
      ]);
      process.exit(ExitCode.USAGE_LIMIT_ERROR);
    }

    // Generic error
    const message = error instanceof Error ? error.message : 'Unknown error';
    printError('Upload Failed', message, [
      'Run with --verbose for more details',
      'Check https://docs.testdino.com for help',
    ]);

    process.exit(ExitCode.GENERAL_ERROR);
  }
}
